import { Suspense, useEffect, useState } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import { routes } from '../../../application/configurations/routesConfig';
import { Footer } from '../../components/footer';
import { Header } from '../../components/header';
import { Disclaimer } from '../../components/disclaimer';
import { useAuthCheck } from '../../../presentation/hooks/auth';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { useAppSelector } from '../../../presentation/hooks/redux';
import { PageLoader } from '../../components/pageLoader';
import { InteractionType } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { appConstants } from '../../../application/configurations/constants';
import { loginRequest } from '../../../application/configurations/authConfig';
import AnalyticsEvents from '../../../infrastructure/services/analytics/analyticsUseCase';
import { AppUtilityService } from './appUtils';

import './index.scss';
import envConfig from '../../../application/configurations/envConfig';

/**
 * Component for activating routes and authentication.
 *
 * @component
 */
declare global {
  var BAI: any;
  var adobeDataLayer: any;
}

const App = () => {
  useAuthCheck();
  const { accounts } = useMsal();
  const [pageLoadedTime, setPageLoadedTime] = useState(0);
  const isLoading = useAppSelector((state) => state.loader.isLoading);
  const userInfo = useAppSelector((state) => state.general.userInfo);

  /**
   * filter all the routes not required authentication
   * @return  {Object} unauthenticated routes only
   */
  const unAuthenticatedRoutes = routes.filter((route) => {
    return route.authRequired ? false : true;
  });

  /**
   * authentication state changes are captured and pass page load time to analytics action
   *
   */
  useEffect(() => {
    if (userInfo && userInfo.userEmail && pageLoadedTime) {
      AnalyticsEvents.pageLoad(pageLoadedTime, accounts);
    }
  }, [userInfo]);

  // This will run one time after the component mounts
  useEffect(() => {
    // Update the object to hold the event values.
    const onPageLoad = () => {
      setPageLoadedTime(performance.now());
    };

    // This will check if the page is loaded from intranet and add the query param to the URL
    let url = window.location.href;

    // Get previous URL
    const previousUrl = document.referrer
    //get the url in this format 'intranet.ey.com' by removing https:// and / from the url
    const formattedUrl = previousUrl.replace('https://', '').replace('/', '');

    if (Array.isArray(envConfig.intranetHostName) && envConfig.intranetHostName.includes(formattedUrl)) {
      if (url.indexOf('?') > -1) {
        if (url.indexOf('c=intranet') === -1) {
          url += '&c=intranet';
        }
      }
      window.location.href = url;
    }

    // Check if the page has already loaded
    if (document.readyState === 'complete') {
      onPageLoad();
    } else {
      window.addEventListener('load', onPageLoad);

      // Remove the event listener when component unmounts
      return () => window.removeEventListener('load', onPageLoad);
    }
  }, []);


  /**
   * reder routes based on the router configurations
   * @return  {Component} route component
   */
  const renderRoutes = (route: {
    path: any | undefined;
    component: any | undefined;
  }) => (
    <Route key={route.path} path={route.path} element={<route.component />} />
  );
  const [hashRouteComparator] = useState(window.location.hash);
  /**
   * templates includes authenticated and unauthenticated routes
   * authenticated routes will be only registered when user is authenticated
   * unauthenticated routes will be registered if user is unauthenticated
   */

  return (
    <div className="discover-app">
      <Suspense fallback={<PageLoader />}>
        {hashRouteComparator !== '#' + appConstants.routePaths.logout ? (
          <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={loginRequest}
            loadingComponent={PageLoader}
          >
            <Header />
            <PageLoader isLoading={isLoading} />
            <HashRouter>
              <Routes>{routes.map(renderRoutes)}</Routes>
            </HashRouter>
            <Footer />
          </MsalAuthenticationTemplate>
        ) : (
          <HashRouter>
            <Routes>{unAuthenticatedRoutes.map(renderRoutes)}</Routes>
          </HashRouter>
        )}
      </Suspense>
    </div>
  );
};

export default App;
